import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyIcon from "@mui/icons-material/Key";
import axiosInstance from "../../axiosInstance";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "../../redux/features/toast/toast.slice";
import { AppDispatch } from "../../redux/store";

interface ApiKey {
  name: string;
  prefix: string;
  key?: string; // Only present when first created
  created: string;
  expiry_date: string | null;
  revoked: boolean;
}

interface UserPlan {
  planName: string;
  hasApiAccess: boolean;
  apiKeyLimit: number;
  apiCallLimit: number;
  apiCallsUsed: number;
}

function ApiKeysPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyValidDays, setNewKeyValidDays] = useState<string>("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      await fetchApiKeys();
      await fetchUserPlan();
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axiosInstance.get("/users/auth/api-keys");
      setApiKeys(response.data.keys || []);
    } catch (error: any) {
      console.error("Error fetching API keys:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPlan = async () => {
    try {
      const response = await axiosInstance.get("/users/subscriptions/api-access/");
      setUserPlan(response.data);
    } catch (error) {
      console.error("Error fetching user plan:", error);
      // Set default values if API fails
      setUserPlan({
        planName: "Free",
        hasApiAccess: false,
        apiKeyLimit: 0,
        apiCallLimit: 0,
        apiCallsUsed: 0,
      });
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      dispatch(showToast({ message: "Please enter a name for your API key", type: "error" }));
      return;
    }

    try {
      const payload: any = { name: newKeyName };
      
      // Add valid_days only if provided
      if (newKeyValidDays.trim() !== "") {
        const days = parseInt(newKeyValidDays);
        if (isNaN(days) || days <= 0) {
          dispatch(showToast({ message: "Valid days must be a positive number", type: "error" }));
          return;
        }
        payload.valid_days = days;
      }

      const response = await axiosInstance.post("/users/auth/api-keys", payload);
      setCreatedKey(response.data.key);
      setSuccessDialogOpen(true);
      setCreateDialogOpen(false);
      setNewKeyName("");
      setNewKeyValidDays("");
      fetchApiKeys();
      dispatch(showToast({ message: response.data.message || "API key created successfully", type: "success" }));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Failed to create API key. Please try again.";
      dispatch(showToast({ message: errorMessage, type: "error" }));
    }
  };

  const handleDeleteKey = async (keyPrefix: string) => {
    try {
      const response = await axiosInstance.delete(`/users/auth/api-keys/${keyPrefix}`);
      dispatch(showToast({ message: response.data.message || "API key revoked successfully", type: "success" }));
      fetchApiKeys();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Failed to revoke API key. Please try again.";
      dispatch(showToast({ message: errorMessage, type: "error" }));
    }
    setDeleteConfirmOpen(false);
    setKeyToDelete(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    dispatch(showToast({ message: "API key copied to clipboard!", type: "success" }));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#FBE5C6",
        }}
      >
        <CircularProgress sx={{ color: "#FF9A56" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#FBE5C6",
        padding: "40px 20px",
      }}
    >
      <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <KeyIcon sx={{ fontSize: 40, color: "#FF9A56" }} />
            <Typography
              variant="h3"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "#282828",
              }}
            >
              API Keys
            </Typography>
            <Button
              variant="text"
              endIcon={<OpenInNewIcon />}
              href="https://api.gplan.in/docs"
              target="_blank"
              sx={{
                color: "#FF9A56",
                fontFamily: "Poppins",
                textTransform: "none",
                fontSize: "16px",
                "&:hover": {
                  backgroundColor: "rgba(28, 76, 130, 0.08)",
                },
              }}
            >
              Documentation
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/home")}
              sx={{
                color: "#FF9A56",
                borderColor: "#FF9A56",
                fontFamily: "Poppins",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#FF9A56",
                  backgroundColor: "rgba(28, 76, 130, 0.08)",
                },
              }}
            >
              Back to Home
            </Button>
          </Stack>
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "16px",
              color: "#949CA9",
            }}
          >
            Manage your API keys to integrate GPLAN services into your applications
          </Typography>
        </Box>

        {/* Plan Status Card */}
        {userPlan && (
          <Card
            sx={{
              mb: 3,
              borderRadius: "12px",
              // boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      color: "#949CA9",
                      mb: 1,
                    }}
                  >
                    Current Plan
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Chip
                      label={userPlan.planName}
                      sx={{
                        backgroundColor: userPlan.hasApiAccess ? "#FF9A56" : "orange",
                        color: "#FBE5C6",
                        fontFamily: "Poppins",
                        fontWeight: 500,
                      }}
                    />
                    <Typography sx={{ fontFamily: "Poppins", color: "#282828", fontWeight: 500 }}>
                      {apiKeys.filter(k => !k.revoked).length} / {userPlan.apiKeyLimit} active API keys
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      color: "#949CA9",
                      textAlign: "right",
                      mb: 1,
                    }}
                  >
                    API Calls This Month
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "20px",
                      fontWeight: 600,
                      color: "#282828",
                      textAlign: "right",
                    }}
                  >
                    {userPlan.apiCallsUsed.toLocaleString()} /{" "}
                    {userPlan.apiCallLimit.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* No API Access Warning */}
        {userPlan && !userPlan.hasApiAccess && (
          <Alert
            severity="warning"
            icon={<InfoOutlinedIcon />}
            sx={{ mb: 3, fontFamily: "Poppins" }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate("/plans")}
                sx={{ textTransform: "none" }}
              >
                View Plans
              </Button>
            }
          >
            Your current plan doesn't include API access. Please upgrade to a paid plan to create and use API keys.
          </Alert>
        )}

        {/* Create API Key Section */}
        <Card
          sx={{
            mb: 3,
            borderRadius: "12px",
            // boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "18px",
                    fontWeight: 500,
                    color: "#282828",
                    mb: 0.5,
                  }}
                >
                  Create New API Key
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    color: "#949CA9",
                  }}
                >
                  Generate a new API key to authenticate your applications
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                disabled={
                  !userPlan?.hasApiAccess ||
                  apiKeys.filter(k => !k.revoked).length >= (userPlan?.apiKeyLimit || 0)
                }
                sx={{
                  backgroundColor: "#3699ff",
                  fontFamily: "Poppins",
                  textTransform: "none",
                  fontSize: "16px",
                  px: 3,
                  "&:hover": {
                    backgroundColor: "#2182e6",
                  },
                  "&:disabled": {
                    backgroundColor: "#949CA9",
                    color: "#FBE5C6",
                  },
                }}
              >
                Create API Key
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* API Keys Table */}
        <Card
          sx={{
            borderRadius: "12px",
            // boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <CardContent>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                color: "#282828",
                mb: 2,
              }}
            >
              Your API Keys
            </Typography>

            {apiKeys.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  color: "#949CA9",
                }}
              >
                <KeyIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                <Typography sx={{ fontFamily: "Poppins", fontSize: "16px", mb: 1 }}>
                  No API keys yet
                </Typography>
                <Typography sx={{ fontFamily: "Poppins", fontSize: "14px" }}>
                  Create your first API key to get started
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
                        Key Prefix
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
                        Created
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
                        Expires
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
                        Status
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontFamily: "Poppins", fontWeight: 600 }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiKeys.map((key) => (
                      <TableRow key={key.prefix} hover sx={{ opacity: key.revoked ? 0.6 : 1 }}>
                        <TableCell sx={{ fontFamily: "Poppins" }}>
                          <Typography sx={{ fontWeight: 500 }}>{key.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "14px",
                                color: "#282828",
                              }}
                            >
                              {key.prefix}
                            </Typography>
                            <Tooltip title="Copy prefix">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(key.prefix)}
                                sx={{ 
                                  ml: 1,
                                  color: "#FF9A56",
                                  "&:hover": {
                                    backgroundColor: "rgba(28, 76, 130, 0.08)",
                                  },
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Poppins" }}>
                          {formatDate(key.created)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Poppins" }}>
                          {formatDate(key.expiry_date)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={key.revoked ? "Revoked" : "Active"}
                            size="small"
                            sx={{
                              backgroundColor: key.revoked ? "#f64e60" : "#FF9A56",
                              color: "#FBE5C6",
                              fontFamily: "Poppins",
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={key.revoked ? "Already revoked" : "Revoke API Key"}>
                            <span>
                              <IconButton
                                disabled={key.revoked}
                                onClick={() => {
                                  setKeyToDelete(key.prefix);
                                  setDeleteConfirmOpen(true);
                                }}
                                sx={{
                                  color: "#f64e60",
                                  "&:hover": {
                                    backgroundColor: "rgba(246, 78, 96, 0.08)",
                                  },
                                  "&:disabled": {
                                    color: "#949CA9",
                                  },
                                }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Security Best Practices Card */}
        <Card
          sx={{
            mt: 3,
            borderRadius: "12px",
            // boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            backgroundColor: "#fff3e0",
            border: "1px solid #ffe0b2",
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <InfoOutlinedIcon sx={{ color: "orange", mt: 0.5, fontSize: 28 }} />
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#282828",
                    mb: 1,
                  }}
                >
                  Security Best Practices
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    color: "#282828",
                  }}
                >
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    <li>Store your API keys securely and never commit them to version control</li>
                    <li>Use environment variables to manage API keys in your applications</li>
                    <li>Rotate your API keys regularly and delete unused keys</li>
                    <li>Each API key will only be shown once upon creation - make sure to copy it</li>
                    <li>Monitor your API usage to detect any unauthorized access</li>
                  </ul>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Create API Key Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
          Create New API Key
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: "14px",
                color: "#282828",
                mb: 1,
              }}
            >
              Name *
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g., Production API Key"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: "14px",
                color: "#282828",
                mb: 1,
              }}
            >
              Expiry (Optional)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="Number of days until expiry (leave empty for no expiry)"
              value={newKeyValidDays}
              onChange={(e) => setNewKeyValidDays(e.target.value)}
              helperText="Leave empty for a key that never expires"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            sx={{
              fontFamily: "Poppins",
              textTransform: "none",
              color: "#949CA9",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateKey}
            variant="contained"
            sx={{
              fontFamily: "Poppins",
              textTransform: "none",
              backgroundColor: "#3699ff",
              "&:hover": {
                backgroundColor: "#2182e6",
              },
            }}
          >
            Create Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog - Show Created Key */}
      <Dialog
        open={successDialogOpen}
        onClose={() => {
          setSuccessDialogOpen(false);
          setCreatedKey(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: "Poppins",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: "#FF9A56", fontSize: 32 }} />
          API Key Created Successfully
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, fontFamily: "Poppins" }}>
            Make sure to copy your API key now. You won't be able to see it again!
          </Alert>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "14px",
                color: "#282828",
                wordBreak: "break-all",
                flex: 1,
              }}
            >
              {createdKey}
            </Typography>
            <Tooltip title="Copy to clipboard">
              <IconButton 
                onClick={() => createdKey && copyToClipboard(createdKey)}
                sx={{
                  color: "#FF9A56",
                  "&:hover": {
                    backgroundColor: "rgba(28, 76, 130, 0.08)",
                  },
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setSuccessDialogOpen(false);
              setCreatedKey(null);
            }}
            variant="contained"
            sx={{
              fontFamily: "Poppins",
              textTransform: "none",
              backgroundColor: "#FF9A56",
              "&:hover": {
                backgroundColor: "#17a89a",
              },
            }}
          >
            I've Saved My Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 600 }}>
          Revoke API Key?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Poppins", color: "#666" }}>
            This will revoke the API key immediately. Applications using this API key will no longer be able
            to access the GPLAN API. The key will be automatically deleted after the retention period.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{
              fontFamily: "Poppins",
              textTransform: "none",
              color: "#949CA9",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => keyToDelete && handleDeleteKey(keyToDelete)}
            variant="contained"
            sx={{
              fontFamily: "Poppins",
              textTransform: "none",
              backgroundColor: "#f64e60",
              "&:hover": {
                backgroundColor: "#d63b4c",
              },
            }}
          >
            Revoke Key
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ApiKeysPage;

